/**
 * Export previews — server state.
 *
 * No `staleTime`: a preview is a claim about what is about to be downloaded,
 * and a cached one that is thirty seconds behind would describe a file
 * different from the one that arrives. The dashboard can afford to be slightly
 * stale because nobody acts on it in the next second; this is read immediately
 * before an action.
 */

import { useQuery } from '@tanstack/react-query';
import { fetchExportPreview } from '../services/api/exports';
import type { ExportDataset, ExportPreview, ExportQuery } from '../services/api/exports';
import { exportKeys } from './queryKeys';

export { exportKeys };

/**
 * @param enabled Whether to fetch at all.
 *
 * The screen passes its role check here. A hook has to be called
 * unconditionally, so without this the preview is requested before the page
 * has decided whether the viewer may see it — and a preview of the volunteer
 * directory is the directory. Letting the API's 403 be the only thing stopping
 * it is leaving the first lock open and trusting the second.
 */
export function useExportPreview(
  dataset: ExportDataset,
  query: ExportQuery = {},
  enabled = true
) {
  return useQuery<ExportPreview>({
    queryKey: exportKeys.preview(dataset, query.from, query.to),
    queryFn: () => fetchExportPreview(dataset, query),
    enabled,
  });
}
